import { Box, Paper, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getChartSliceColor } from "../../../utils/distinctColors";

type SummaryBarChartProps = {
  title: string;
  items: { name: string; value: number }[];
  formatValue: (value: number) => string;
  minHeight?: number;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: { name: string; value: number };
  }>;
  formatValue: (value: number) => string;
};

function ChartTooltip({ active, payload, formatValue }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        zIndex: 20,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "#fff",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#182737" }}>
        {item.name}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#182737", mt: 0.25 }}>
        {formatValue(item.value)}
      </Typography>
    </Paper>
  );
}

const ROW_HEIGHT = 32;

export function SummaryBarChart({
  title,
  items,
  formatValue,
  minHeight = 280,
}: SummaryBarChartProps) {
  const chartData = items
    .filter((item) => item.value > 0)
    .map((item, index) => ({
      ...item,
      fill: getChartSliceColor(index),
    }));

  if (chartData.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          height: "100%",
          minHeight,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 2.5,
          py: 2,
        }}
      >
        <Typography className="text-general-section-title" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#182737" }}>
          No data for the current filters.
        </Typography>
      </Paper>
    );
  }

  const chartHeight = Math.max(chartData.length * ROW_HEIGHT + 16, 140);

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        background:
          "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography className="text-general-section-title">
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 1, md: 1.5 },
          py: 1.5,
          overflowY: "auto",
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 72, left: 4, bottom: 0 }}
            barCategoryGap="22%"
          >
            <XAxis type="number" hide domain={[0, "dataMax"]} />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{
                fill: "#182737",
                fontSize: 11,
                fontWeight: 500,
              }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<ChartTooltip formatValue={formatValue} />}
              cursor={{ fill: "rgba(14, 70, 100, 0.06)" }}
              wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
            />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={12}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
              <LabelList
                dataKey="value"
                position="right"
                formatter={(value) =>
                  typeof value === "number" ? formatValue(value) : value
                }
                style={{
                  fill: "#182737",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
